import type { MarketType, OrderBookLevels } from '@/utils/types'
import isUndefined from 'lodash/isUndefined'
import isNull from 'lodash/isNull'
import { Decimal } from 'decimal.js'
import { z } from 'zod'

export function calcPriceLevel(
  orderbook: OrderBookLevels,
  amount: number,
  amMultiplier = 1,
): [number, number] | null {
  let collectedVol = 0
  amount = amount * amMultiplier // usdt amount

  for (const [price, size] of orderbook) {
    const levelVol = size * price
    collectedVol += levelVol
    if (amount <= collectedVol) return [price, collectedVol]
  }
  return null
}

// eslint-disable-next-line
export function logReturns(prices: any) {
  const returns = []
  for (let i = 1; i < prices.length; i++) {
    const ret = Math.log(prices[i] / prices[i - 1])
    returns.push(ret)
  }
  return returns
}

export function getStdev(array: number[]) {
  const n = array.length
  const mean = array.reduce((a, b) => a + b) / n
  return [mean, Math.sqrt(array.map((x) => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / n)]
}

export function findMostFrequent<T>(arr: T[]): T | undefined {
  if (arr.length === 0) {
    return undefined
  }

  // Create a frequency map to count occurrences of each element
  const frequencyMap: Map<T, number> = new Map()
  for (const item of arr) {
    frequencyMap.set(item, (frequencyMap.get(item) || 0) + 1)
  }

  let maxCount = 0
  let mostFrequentElement: T | undefined

  // Iterate over the frequency map to find the element with the highest count
  for (const [element, count] of frequencyMap.entries()) {
    if (count > maxCount) {
      maxCount = count
      mostFrequentElement = element
    }
  }

  return mostFrequentElement
}

export const readSettingsNumber = (
  value: string | number | undefined | null,
  cacheKey: string,
  forceReadCache: boolean = false,
) => {
  if (forceReadCache) {
    const value3 = localStorage.getItem(cacheKey)
    if (!isUndefined(value3)) {
      return value3 ? Number(value3) : null
    } else {
      return undefined
    }
  }

  const value2 = value
  if (value === '') {
    return null
  }
  if (isUndefined(value2)) {
    const value3 = localStorage.getItem(cacheKey)
    if (!isUndefined(value3)) {
      return value3 ? Number(value3) : null
    } else {
      return undefined
    }
  }
  return value2 ? Number(value2) : null
}

export const sleep = (ms: number) => new Promise((r) => window.setTimeout(r, ms))

export function iOS() {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
      navigator.platform,
    ) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  )
}

export function secondsLeftToUtc(
  isoUtc: string | null | undefined,
  customDate: Date,
): number | null {
  if (isNull(isoUtc) || isUndefined(isoUtc)) {
    return null
  }
  if (!isoUtc.endsWith('Z')) {
    isoUtc += 'Z'
  }
  const target = new Date(isoUtc) // ISO string is parsed as UTC automatically
  return Math.round((target.getTime() - customDate.getTime()) / 1000)
}

export function secondsToHhMmSs(totalSeconds: number): string {
  const abs = Math.abs(totalSeconds)
  const h = Math.floor(abs / 3600)
  const m = Math.floor((abs % 3600) / 60)
  const s = abs % 60

  const sign = totalSeconds < 0 ? '-' : ''
  return `${sign}${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export const camelToSnake = (str: string) =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)

export function keysToSnake<T extends Record<string, unknown>>(obj: T): T {
  if (Array.isArray(obj)) return obj.map(keysToSnake) as unknown as T
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [
        camelToSnake(k),
        typeof v === 'object' ? keysToSnake(v as Record<string, unknown>) : v,
      ]),
    ) as T
  }
  return obj
}

export function toK(num: number) {
  if (Math.abs(num) < 1000) return Number(num).toFixed(0)
  const k = Math.floor(Math.log10(Math.abs(num)) / 3)
  const shrunk = (num / Math.pow(1000, k)).toFixed(1)
  const clean = shrunk.replace(/\.0$/, '')
  const suffix = 'k'.repeat(k)
  return clean + suffix
}

export function yyyymmdd(date: Date) {
  const mm = date.getMonth() + 1 // getMonth() is zero-based
  const dd = date.getDate()
  return [(dd > 9 ? '' : '0') + dd, (mm > 9 ? '' : '0') + mm, date.getFullYear()].join('.')
}

export function hhmmss(date: Date) {
  const hh = date.getHours()
  const mm = date.getMinutes()
  const ss = date.getSeconds()
  return [(hh > 9 ? '' : '0') + hh, (mm > 9 ? '' : '0') + mm, (ss > 9 ? '' : '0') + ss].join(':')
}

export function hhmm(date: Date) {
  const hh = date.getHours()
  const mm = date.getMinutes()
  return [(hh > 9 ? '' : '0') + hh, (mm > 9 ? '' : '0') + mm].join(':')
}

export function dateFullStr(date: Date) {
  return yyyymmdd(date) + ' ' + hhmmss(date)
}

export function numberWithCommas(x: number | string) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export function spreadHelper(spread: number | null) {
  if (isNull(spread) || isUndefined(spread)) {
    return null
  }
  const result = Number(spread).toFixed(2)
  if (result == '-0.00') {
    return '0.00'
  }
  return result
}

export function calcSpread(long: number, short: number, backSpread = false) {
  if (!backSpread) {
    return (short / long - 1) * 100
  } else {
    return (1 - long / short) * 100
  }
}

export function getDexTradeUrl(chain: string, contract: string) {
  if (chain) {
    chain = chain.toLowerCase()
    if (chain == 'sol') {
      chain = 'solana'
    } else if (chain == 'eth') {
      chain = 'ethereum'
    } else if (chain == 'bep20') {
      chain = 'bsc'
    }
  }
  return `https://dexscreener.com/${chain}/${contract}`
}

export const split = (x: string | null | undefined) => {
  if (!x || x.length === 0) {
    return []
  }
  return x.split(',')
}

export const parseCommaSeparatedAssets = (x: string | null | undefined): string[] | null => {
  if (!x || x.trim().length === 0) {
    return null
  }
  return x
    .split(',')
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean)
}

export const strToNumber = (x: string | null | undefined, defaultValue: null | number = null) => {
  if (isNull(x) || isUndefined(x)) {
    return defaultValue
  }
  const numb = Number(x)
  if (isNaN(numb)) {
    return defaultValue
  }
  return numb
}

export function getTradeUrl(
  exchange: string | null | undefined,
  asset: string,
  swap: boolean,
  quote: string = 'USDT',
): string {
  if (!asset || !exchange) return ''

  const left = asset.toUpperCase()
  const right = quote.toUpperCase()

  switch (exchange.toLowerCase()) {
    case 'lbank': {
      const l = left.toLowerCase()
      const r = right.toLowerCase()
      return swap
        ? `https://www.lbank.com/futures/${l}${r}`
        : `https://www.lbank.com/trade/${l}_${r}`
    }

    case 'binance': {
      return swap
        ? `https://www.binance.com/en/futures/${left}${right}`
        : `https://www.binance.com/en/trade/${left}_${right}?type=spot`
    }

    case 'mexc':
      return swap
        ? `https://futures.mexc.com/exchange/${left}_${right}`
        : `https://www.mexc.com/exchange/${left}_${right}`

    case 'kucoin':
      return swap
        ? `https://www.kucoin.com/futures/trade/${left}${right}M`
        : `https://www.kucoin.com/trade/${left}-${right}`

    case 'gate':
      return swap
        ? `https://www.gate.io/futures/USDT/${left}_${right}`
        : `https://www.gate.io/trade/${left}_${right}`

    case 'bitget':
      return swap
        ? `https://www.bitget.com/futures/usdt/${left}${right}`
        : `https://www.bitget.com/spot/${left}${right}`
    case 'xt': {
      const l = left.toLowerCase()
      const r = right.toLowerCase()
      return swap
        ? `https://www.xt.com/ru/futures/trade/${l}_${r}`
        : `https://www.xt.com/ru/trade/${l}_${r}`
    }

    case 'bingx':
      return swap
        ? `https://swap.bingx.com/en-us/${left}-${right}/`
        : `https://bingx.com/en-us/spot/${left}${right}/`

    case 'bitmart':
      return swap
        ? `https://derivatives.bitmart.com/en-US?symbol=${left}${right}&theme=dark`
        : `https://www.bitmart.com/trade/en-US?layout=pro&theme=dark&symbol=${left}_${right}`

    case 'okx': {
      const pair = `${left}-${right}`.toLowerCase()
      return swap
        ? `https://www.okx.com/trade-swap/${pair}-swap`
        : `https://www.okx.com/trade-spot/${pair}`
    }

    case 'coinex': {
      const pair = `${left}-${right}`.toLowerCase()
      return swap
        ? `https://www.coinex.com/futures/${pair}`
        : `https://www.coinex.com/exchange/${pair}`
    }

    case 'bitrue': {
      const l = left.toLowerCase()
      const r = right.toLowerCase()
      return swap
        ? `https://www.bitrue.com/futures/${l}`
        : `https://www.bitrue.com/ru/trade/${l}_${r}`
    }

    case 'bitunix': {
      return swap
        ? `https://www.bitunix.com/ru-ru/contract-trade/${left}${right}`
        : `https://www.bitunix.com/ru-ru/spot-trade/${left}${right}`
    }

    case 'bybit':
      return swap
        ? `https://www.bybit.com/trade/usdt/${left}${right}`
        : `https://www.bybit.com/en/trade/spot/${left}/${right}`

    case 'poloniex': {
      const pair = `${left}_${right}`
      return swap
        ? `https://poloniex.com/futures/trade/${pair}PERP`
        : `https://poloniex.com/trade/${left}_${right}?type=spot`
    }

    case 'htx': {
      const spotPair = `${left.toLowerCase()}_${right.toLowerCase()}`
      const futuresPair = `${left}-${right}`
      return swap
        ? `https://futures.htx.com/en-us/futures/linear_swap/exchange#contract_code=${futuresPair}&contract_type=swap&type=isolated`
        : `https://www.htx.com/en-us/trade/${spotPair}?type=spot`
    }

    case 'ourbit':
      return swap
        ? `https://futures.ourbit.com/exchange/${left}_${right}?type=linear_swap`
        : `https://www.ourbit.com/exchange/${left}_${right}`

    case 'blofin':
      return swap
        ? `https://blofin.com/futures/ru/${left}-${right}`
        : `https://blofin.com/spot/ru/${left}-${right}`

    case 'weex':
      return swap
        ? `https://www.weex.com/futures/${left}-${right}`
        : `https://www.weex.com/spot/${left}-${right}`

    case 'toobit':
      return swap
        ? `https://www.toobit.com/ru-RU/futures/${left}-SWAP-${right}`
        : `https://www.toobit.com/ru-RU/spot/${left}_${right}`

    case 'hyperliquid':
      return swap ? `https://app.hyperliquid.xyz/trade/${left}` : ''

    case 'dydx':
      return swap ? `https://dydx.trade/trade/${left}-USD` : ''

    case 'aster':
      return swap ? `https://www.asterdex.com/en/futures/v1/${left}USDT` : ''

    case 'phemex':
      return swap
        ? `https://phemex.com/ru/futures/${left}-${right}`
        : 'https://phemex.com/ru/trade/${left}-${right}'

    default:
      return ''
  }
}

export const booleanFormatter = (v: string) => v == '1'
export const numberFormatter = (v: string) => Number(v)
export const numberListFormatter = (v: string) => v.split(';').map((x) => Number(x))
export const stringListFormatter = (v: string) => v.split(';').map((x) => x)
export const stringFormatter = (v: string) => v
export const jsonFormatter = (v: string) => JSON.parse(v)

export function toBigInt(value: string | number) {
  let retValue = value
  if (typeof retValue === 'number') {
    retValue = Math.floor(retValue).toLocaleString('fullwide', {
      useGrouping: false,
      maximumFractionDigits: 50,
    })
  }
  return BigInt(retValue)
}

export function loadFromStorage<T>(
  key: string,
  formatter: (arg9: string) => unknown,
  defaultValue: string | number | boolean | object | null,
) {
  const k = localStorage.getItem(key)
  if (!k) {
    return defaultValue as T
  }
  return formatter(k) as T
}

export function calcDecimals(v: number) {
  const fixed = Number(v).toFixed(16)
  return fixed.split('.')[1]?.replace(/[0]+$/g, '').length as number
}

export const convertPeriodStr = (v: string | null) => {
  const cv = convertPeriod(v)
  if (!cv) {
    return '—'
  }
  return cv
}

export const convertPeriod = (v: string | null | unknown) => {
  if (isNull(v) || isUndefined(v)) {
    return null
  }
  const sexLeft = secondsLeftToUtc(v as string, new Date())
  if (!sexLeft || sexLeft < 0) {
    return null
  }
  return secondsToHhMmSs(sexLeft)
}

export function buildDefaultChartOptions(
  yFormat: string | null | undefined,
  tzOffsetMs: number = 0,
) {
  const textColor = getComputedStyle(document.documentElement).getPropertyValue('--sr-text-color')
  const borderColor = getComputedStyle(document.documentElement).getPropertyValue(
    '--sr-border-color-2',
  )
  return {
    autoSize: true,
    rightPriceScale: {
      visible: true,
      borderColor: borderColor,
      drawTicks: true,
      ticksVisible: true,
      borderVisible: true,
      textColor: textColor,
    },
    layout: {
      background: { color: 'transparent' },
      textColor: textColor,
      panes: {
        enableResize: true,
        separatorColor: '#2B2B43',
        separatorHoverColor: 'rgba(178, 181, 189, 0.2)',
      },
    },
    grid: {
      vertLines: { color: '#aaaaaa30' },
      horzLines: { color: '#aaaaaa30' },
    },
    localization: {
      timeFormatter: (time: number) => {
        return customChartTimeFormatter(yFormat, time, tzOffsetMs)
      },
    },
    timeScale: {
      borderColor: borderColor,
      tickMarkFormatter: (time: number) => {
        return customChartTimeFormatter(yFormat, time, tzOffsetMs)
      },
      timeVisible: true,
      secondsVisible: false,
    },
  }
}

export function customChartTimeFormatter(
  yFormat: string | null | undefined,
  time: number,
  tzOffsetMs: number = 0,
) {
  const dObj = new Date(time * 1000 + tzOffsetMs)
  let hours = dObj.getUTCHours().toString()
  let minutes = dObj.getUTCMinutes().toString()
  const day = dObj.getUTCDate()
  const month = dObj.getUTCMonth() + 1

  if (hours.length == 1) {
    hours = `0${hours}`
  }
  if (minutes.length == 1) {
    minutes = `0${minutes}`
  }
  if (yFormat == '1s') {
    let seconds = dObj.getUTCSeconds().toString()
    if (seconds.length == 1) {
      seconds = `0${seconds}`
    }
    return `${hours}:${minutes}:${seconds}`
  } else {
    return `${day}/${month} ${hours}:${minutes}`
  }
}

export function preventEnter(e: KeyboardEvent) {
  if (e.key.toLowerCase() == 'enter') {
    e.preventDefault()
  }
}

export function getGmgnUrl(chain: string, address: string) {
  chain = chain.toLowerCase()
  return `https://gmgn.ai/${chain}/token/${address}`
}

const innerChainToDexScreener = (v: string) => {
  v = v.toLowerCase()
  if (v == 'sol') {
    return 'solana'
  }
  return v
}

export function getDexUrl(dev: string, chain: string | undefined, address: string) {
  dev = dev.toLowerCase()
  chain = (chain ?? 'bsc').toLowerCase()
  if (dev == 'uniswap') {
    // TODO: convert chain to uniswap format
    return `https://app.uniswap.org/explore/tokens/${chain}/${address}?inputCurrency=NATIVE`
  } else if (dev == 'raydium') {
    return `https://raydium.io/swap/?inputMint=sol&outputMint=${address}`
  } else if (dev == 'pumpfun') {
    return `https://swap.pump.fun/?input=So11111111111111111111111111111111111111112&output=${address}`
  } else if (dev == 'meteora') {
    return `https://www.meteora.ag/dlmm/${address}`
  } else if (dev == 'pancakeswap') {
    return `https://pancakeswap.finance/swap?outputCurrency=${address}`
  }
  return `https://dexscreener.com/${innerChainToDexScreener(chain)}/${address}`
}

export function noExponents(value: number) {
  if (value === null) {
    return null
  }
  const data = String(value).split(/[eE]/)
  if (data.length == 1) return data[0]

  let z = ''
  const sign = value < 0 ? '-' : ''
  const str = data[0]?.replace('.', '')
  if (!str) {
    return null
  }
  let mag = Number(data[1]) + 1

  if (mag < 0) {
    z = sign + '0.'
    while (mag++) z += '0'

    return z + str.replace(/^\-/, '')
  }
  mag -= str.length
  while (mag--) z += '0'
  return str + z
}

/**
 * Форматирует очень маленькие положительные числа в компактный вид:
 * 0.00000000213 → 0.0(8)213
 * Если число >= threshold (по умолчанию 0.0001), возвращает noExponents.
 */
export function fmtSmallNumber(value: number | null, threshold = 0.0001): string | null {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return null
  }
  if (value === 0) {
    return '0'
  }

  const absVal = Math.abs(value)
  if (absVal >= threshold) {
    return noExponents(value) ?? null
  }

  const str = noExponents(value)
  if (!str) {
    return null
  }

  const match = str.match(/^(-?)0\.(0+)(\d+)$/)
  if (!match) {
    return str
  }

  const sign = match[1] || ''
  const zeros = match[2]!.length
  const digits = match[3]
  return `${sign}0.0(${zeros})${digits}`
}

function gcd(a: Decimal, b: Decimal): Decimal {
  // Work with absolute values
  a = a.abs()
  b = b.abs()

  // Handle zero cases
  if (b.isZero()) return a
  if (a.isZero()) return b

  // Classic Euclidean algorithm
  while (!b.isZero()) {
    const temp = b
    b = a.mod(b)
    a = temp
  }

  return a
}

export function lcm(a: number | string, b: number | string): number {
  const decA = new Decimal(a)
  const decB = new Decimal(b)

  // LCM = |a × b| / GCD(a, b)
  const product = decA.mul(decB).abs()
  const gcdValue = gcd(decA, decB)

  // If either input is 0, LCM is 0
  if (decA.isZero() || decB.isZero()) {
    return 0
  }
  return product.div(gcdValue).toNumber()
}

export const srZodNumber = () =>
  z.union([
    z
      .string()
      .transform((value) => (value === '' ? null : value))
      .nullable()
      .refine((value) => value === null || !isNaN(Number(value)), {
        message: 'numberError',
      }),
    z.number({ error: 'numberError' }).nullable(),
  ])

export function getMean(array: number[]) {
  if (array.length === 0) return 0 // Avoid division by zero
  const sum = array.reduce((accumulator, currentValue) => accumulator + currentValue, 0)
  return sum / array.length
}

export function getBuyMarketType(marketType: MarketType) {
  if (['spot', 'spot-swap'].includes(marketType)) {
    return 'spot'
  } else {
    return 'swap'
  }
}

export function getSellMarketType(marketType: MarketType) {
  if (['spot', 'swap-spot'].includes(marketType)) {
    return 'spot'
  } else {
    return 'swap'
  }
}
